import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MaskLines } from "@/components/site/Reveal";

function AuthForm() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f.name, f.email, f.password);
      toast("Welcome to Melic");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const input = "mt-2 h-12 w-full border-b border-[var(--ink)]/30 bg-transparent text-base outline-none focus:border-[var(--terra)]";

  return (
    <div className="mx-auto max-w-md px-5 pb-32 pt-16 md:pt-24" data-testid="auth-page">
      <MaskLines as="h1" className="font-display text-5xl tracking-tight sm:text-6xl" lines={[{ text: mode === "login" ? "Welcome back." : "Join Melic." }]} key={mode} />
      <div className="mt-8 flex gap-2">
        {["login", "register"].map((m) => (
          <button key={m} onClick={() => { setMode(m); setError(""); }} className={`rounded-full px-5 py-2 text-sm font-medium ${mode === m ? "bg-[var(--ink)] text-[var(--bone)]" : "border border-[var(--line)]"}`} data-testid={`auth-tab-${m}`}>
            {m === "login" ? "Sign in" : "Create account"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="mt-10 space-y-8">
        {mode === "register" && (
          <label className="block"><span className="eyebrow !text-[10px] text-[var(--ink-soft)]">Name</span>
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="auth-name-input" /></label>
        )}
        <label className="block"><span className="eyebrow !text-[10px] text-[var(--ink-soft)]">Email</span>
          <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} data-testid="auth-email-input" /></label>
        <label className="block"><span className="eyebrow !text-[10px] text-[var(--ink-soft)]">Password</span>
          <input required type="password" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={input} data-testid="auth-password-input" /></label>
        {error && <p className="text-sm text-red-700" data-testid="auth-error">{error}</p>}
        <button disabled={busy} className="w-full rounded-full bg-[var(--terra)] py-4 text-sm font-semibold text-[#241a06] transition-colors hover:bg-[var(--terra-deep)] disabled:opacity-60" data-testid="auth-submit-button">
          {busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>
    </div>
  );
}

const statusTone = { paid: "bg-[var(--sand)]", processing: "bg-amber-100", shipped: "bg-sky-100", delivered: "bg-emerald-100", cancelled: "bg-red-100" };

function Orders() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => setOrders([]));
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-5 pb-32 pt-16 md:px-10 md:pt-24" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-[var(--terra-deep)]">Your account</p>
          <h1 className="font-display mt-3 text-5xl tracking-tight" data-testid="account-greeting">Hi, {user.name || user.email}</h1>
        </div>
        <div className="flex gap-3">
          {user.role === "admin" && <Link to="/admin" className="rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-[var(--bone)]" data-testid="account-admin-link">Admin dashboard</Link>}
          <button onClick={logout} className="rounded-full border border-[var(--ink)] px-5 py-2.5 text-sm font-semibold" data-testid="logout-button">Sign out</button>
        </div>
      </div>
      <p className="eyebrow mt-16 text-[var(--ink-soft)]">Order history</p>
      {orders && orders.length === 0 && <p className="mt-6 text-[var(--ink-soft)]" data-testid="orders-empty">No orders yet. <Link to="/shop" className="link-u font-semibold text-[var(--ink)]">Start shopping</Link></p>}
      <div className="mt-6 space-y-4">
        {(orders || []).map((o) => (
          <div key={o.id} className="rounded-2xl border border-[var(--line)] p-6" data-testid={`order-${o.number}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono-x font-medium">{o.number}</span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusTone[o.status] || ""}`}>{o.status}</span>
            </div>
            <p className="mt-2 text-sm text-[var(--ink-soft)]">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item(s) · {money(o.total)}</p>
            <div className="mt-4 flex gap-2">{o.items.map((i) => <img key={i.product_id} src={i.image} alt={i.name} className="h-14 w-12 rounded-md object-cover" />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Account() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-[60vh]" />;
  return user ? <Orders /> : <AuthForm />;
}
